// ============================================================================
// OBT — Kampanya BELGELİ KALICI silme diyaloğu (çift onay + zorunlu sebep)
// ============================================================================
// Backend sözleşmesi: POST /obt/campaigns/{id}/delete { reason }. Yetki backend'de
// (admin ya da sahibi). Silme KALICIDIR (arşivden farklı): kampanya aktif+arşiv
// görünümünden düşer, geriye künye (kim/ne zaman/neden) kalır ve işlem geçmişinde
// görünür. Yanlışlıkla silmeyi önlemek için İKİ adımlı onay + zorunlu gerekçe.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { deleteCampaign } from './campaignApi';
import { normalizeObtError } from '../obtErrors';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {{campaign_id:string, title?:string}|null} props.campaign
 * @param {() => void} props.onClose
 * @param {() => void} [props.onDeleted]
 */
export default function DeleteCampaignDialog({ open, campaign, onClose, onDeleted }) {
  const [step, setStep] = useState(1); // 1: sebep, 2: son onay
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setReason('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const trimmed = reason.trim();
  const canContinue = trimmed.length > 0;

  const handleDelete = async () => {
    if (!campaign?.campaign_id || !canContinue || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteCampaign(campaign.campaign_id, { reason: trimmed });
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) {
      setError(normalizeObtError(e));
      setSubmitting(false);
    }
  };

  const name = campaign?.title ? `“${campaign.title}”` : 'Bu bilgi talebi';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            Bilgi talebini kalıcı sil
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? `${name} aktif ve arşiv listesinden kalıcı olarak kaldırılacak. Bu işlem geri alınamaz; yalnızca kim, ne zaman ve hangi gerekçeyle sildiğiniz işlem geçmişinde saklanır.`
              : 'Son onay: Bu işlem geri alınamaz.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-2">
            <Label htmlFor="obt-delete-reason">Silme gerekçesi (zorunlu)</Label>
            <textarea
              id="obt-delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Örn. Bozuk havuzlar ihale kapsamında yenilendi, bilgi talebine gerek kalmadı."
              className="w-full resize-y rounded-md border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <p className="text-xs text-gray-400">
              Bu gerekçe işlem geçmişinde kalıcı olarak görünecektir.
            </p>
          </div>
        ) : (
          <div className="space-y-2 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-inset ring-red-200">
            <p>
              <strong>{name}</strong> kalıcı olarak silinecek.
            </p>
            <p className="text-xs">
              Gerekçe: <span className="font-medium">{trimmed}</span>
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error.message || 'Bilgi talebi silinemedi.'}
          </p>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Vazgeç
              </Button>
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setStep(2)}
                disabled={!canContinue}
              >
                Devam
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={submitting}>
                Geri
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Evet, kalıcı sil
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
